import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCandleHolder,
  faMoon,
  faSun,
} from '@fortawesome/pro-duotone-svg-icons'

import { ThemeMode } from '../../_hooks/theme-context-types'
import { CommandEntry, ScoredEntry } from './types'

/**
 * Picks the icon that represents the current theme in the palette
 * "switch theme" entry.
 *
 * @param {ThemeMode} theme - the current theme mode
 * @returns {IconDefinition} the icon for that theme
 */
const themeGlyphFor = (theme: ThemeMode): IconDefinition => {
  if (theme === 'cozy') {
    return faCandleHolder
  }

  if (theme === 'dark') {
    return faMoon
  }

  return faSun
}

/**
 * Picks the renter-facing label for the next theme in the cycle. Used
 * inside the "Switch theme → ..." palette entry.
 *
 * @param {ThemeMode} theme - the current theme mode
 * @returns {string} the next-theme label
 */
const nextThemeLabelFor = (theme: ThemeMode): string => {
  if (theme === 'light') {
    return 'cozy peach'
  }

  if (theme === 'cozy') {
    return 'midnight purple'
  }

  return 'crisp light'
}

/**
 * Filters + ranks command entries against a free-text query. Uses a
 * very simple scoring model: exact matches > prefix matches >
 * contains > keyword matches. Empty query returns the full list in
 * original order.
 *
 * @param {CommandEntry[]} entries - the available commands
 * @param {string} query - the free-text query
 * @returns {CommandEntry[]} the filtered + ranked entries
 */
const filterCommandEntries = (
  entries: CommandEntry[],
  query: string,
): CommandEntry[] => {
  const trimmed = query.trim().toLowerCase()

  if (trimmed === '') {
    return entries
  }

  const scored: ScoredEntry[] = []

  for (const entry of entries) {
    const labelLower = entry.label.toLowerCase()
    const hintLower = entry.hint.toLowerCase()
    let bestScore = 0

    if (labelLower === trimmed) {
      bestScore = 100
    } else if (labelLower.startsWith(trimmed)) {
      bestScore = 80
    } else if (labelLower.includes(trimmed)) {
      bestScore = 60
    } else if (hintLower.includes(trimmed)) {
      bestScore = 40
    } else {
      for (const keyword of entry.keywords) {
        if (keyword.toLowerCase().includes(trimmed)) {
          bestScore = Math.max(bestScore, 30)
        }
      }
    }

    if (bestScore > 0) {
      scored.push({ entry, score: bestScore })
    }
  }

  scored.sort((alpha, beta) => beta.score - alpha.score)
  return scored.map(scoredEntry => scoredEntry.entry)
}

/**
 * Clamps an index into the bounds of a list, wrapping at both ends so
 * arrow-key navigation feels natural.
 *
 * @param {number} index - the proposed index
 * @param {number} length - the length of the list
 * @returns {number} the clamped index
 */
const wrapIndex = (index: number, length: number): number => {
  if (length === 0) {
    return 0
  }

  if (index < 0) {
    return length - 1
  }

  if (index >= length) {
    return 0
  }

  return index
}

export { filterCommandEntries, wrapIndex, themeGlyphFor, nextThemeLabelFor }
